/**
 * What the Activity page shows for the API's data (slice 2c spec §8): times,
 * durations, tokens, what started a turn, its day groups, and which steps to
 * show in what order. Pure, so it's tested without rendering.
 */
import type { ActivityDay, ActivityItem } from "./api";

/** "58s", "1m 12s", "2h 3m"; a step's short one keeps a decimal ("1.4s"). "—" when unknown. */
export function duration(ms: number | null, opts: { precise?: boolean } = {}): string {
  if (ms === null || !Number.isFinite(ms)) return "—";
  const s = ms / 1000;
  if (opts.precise && s < 10) return `${s.toFixed(1)}s`;
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${Math.round(s % 60)}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/** "212k", "1.2M", "980"; "—" when the turn reported none. */
export function tokens(n: number | null): string {
  if (n === null) return "—";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
}

/** The time of day, in the workspace's timezone: "10:42", "7:00". */
export function clock(at: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(at));
}

/** The local day of `at`, as `YYYY-MM-DD`, like the API's `days`. */
function localDate(at: string | Date, timezone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: timezone }).format(new Date(at));
}

/** "Today · Wed, Sep 30", "Yesterday · Tue, Sep 29", "Mon, Sep 28". */
export function dayLabel(date: string, today: string): string {
  const named = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(new Date(`${date}T12:00:00Z`));
  if (date === today) return `Today · ${named}`;
  const yesterday = new Date(`${today}T12:00:00Z`);
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  return date === yesterday.toISOString().slice(0, 10) ? `Yesterday · ${named}` : named;
}

/** " for <name>" when a task's or routine's turn is for someone (slice 6a spec §4.3). */
const forWhom = (started: ActivityItem["started_by"]) => (started.for ? ` for ${started.for}` : "");

/** Slice 5 (spec §11.4): "Routine · Morning note · for Sam". */
function routineLabel({ label, for: whom }: ActivityItem["started_by"]): string {
  return [label ? `Routine · ${label}` : "Routine", whom ? `for ${whom}` : null]
    .filter(Boolean)
    .join(" · ");
}

/** What started a turn, in a row's words (spec §6). */
export function startedBy(started: ActivityItem["started_by"]): string {
  const { kind, label, doc } = started;
  switch (kind) {
    case "person":
      // Slice 2d (spec §8): a message with a doc attached.
      return doc ? `${label ?? "Someone"} · About ${doc.title}` : (label ?? "Someone");
    case "routine":
      return routineLabel(started);
    case "task":
      return label ? `Task · ${label}${forWhom(started)}` : "Task";
    case "approval":
      return label ? `Approval · ${label}` : "Approval";
    case "nudge":
      return started.for ? `Nudge · for ${started.for}` : "Nudge";
    case "retry":
      return label ? `Retry · ${label}` : "Retry";
    case "unexpected":
      return "Not started by Crew";
    // Slice 6c: I'm done, Skip or Ask again on a handoff.
    case "handoff":
      return label ? `Handoff · ${label}` : "Handoff";
  }
}

/** The receipt's "who and why" line (spec §6). */
export function whoAndWhy(item: Pick<ActivityItem, "started_by" | "agent">): string {
  const who = item.agent?.name ?? "An agent";
  const { kind, label, task, doc } = item.started_by;
  const by = label ?? "someone";
  const title = task?.title ?? "a task";
  const forThem = forWhom(item.started_by);
  switch (kind) {
    case "person":
      if (doc) return `${who} · asked by ${by} about ${doc.title}`;
      return `${who} · asked by ${by} in a chat`;
    case "routine":
      return label
        ? `${who} · started by the routine ${label}${forThem}`
        : `${who} · started by a routine${forThem}`;
    case "task":
      if (label === "carrying on") return `${who} · carrying on with ${title}${forThem}`;
      if (label === "starting") return `${who} · starting ${title}${forThem}`;
      return `${who} · asked as a new task by ${by}`;
    case "approval":
      return label === "Expiry"
        ? `${who} · carrying on after an approval expired`
        : `${who} · carrying on after ${by} answered an approval`;
    case "nudge":
      return `${who} · checked on ${title}${forThem} after a quiet spell`;
    case "retry":
      return `${who} · retried by ${by}`;
    case "unexpected":
      return `${who} · not started by Crew: no person, routine or task behind it`;
    case "handoff":
      return `${who} · carrying on after ${by} used the computer`;
  }
}

export type RowState = "working" | "done" | "failed" | "unexpected";

export function rowState(item: Pick<ActivityItem, "status" | "type">): RowState {
  if (item.type === "unexpected") return "unexpected";
  if (item.status === "queued" || item.status === "running") return "working";
  return item.status === "failed" ? "failed" : "done";
}

/** A failed turn's reason, in words. */
export function failure(reason: string | null): string {
  switch (reason) {
    case "couldnt_start":
      return "Couldn't start";
    case "ai_auth":
      return "The AI signed out";
    case "interrupted":
      return "Interrupted by a restart";
    case "stopped":
      return "Stopped partway";
    default:
      return "Failed";
  }
}

/** The step kinds a row shows, in this order (spec §8.1). */
const STEP_ORDER = [
  "search",
  "page",
  "browser",
  "file",
  "note",
  "command",
  "message",
  "tasks",
  "docs",
  "other",
];

export function stepCounts(steps: Record<string, number>): { kind: string; n: number }[] {
  return STEP_ORDER.filter((k) => (steps[k] ?? 0) > 0).map((kind) => ({ kind, n: steps[kind] }));
}

/** Steps a receipt shows before "Show N more" (§8.2). */
const STEPS_SHOWN = 3;

/**
 * How many of a receipt's steps "Show N more" holds back: none when that
 * would be one step, since the button would take its place.
 */
export function stepsHeld(count: number): number {
  const held = count - STEPS_SHOWN;
  return held > 1 ? held : 0;
}

export type DayGroup = {
  date: string;
  label: string;
  day: ActivityDay | null;
  items: ActivityItem[];
};

/** The items in local days, newest first, each with its totals from `days`. */
export function byDay(
  items: ActivityItem[],
  days: ActivityDay[],
  timezone: string,
  now: Date = new Date(),
): DayGroup[] {
  const today = localDate(now, timezone);
  const totals = new Map(days.map((d) => [d.date, d]));
  const groups: DayGroup[] = [];
  for (const item of items) {
    const date = localDate(item.at, timezone);
    let group = groups.at(-1);
    if (group?.date !== date) {
      group = { date, label: dayLabel(date, today), day: totals.get(date) ?? null, items: [] };
      groups.push(group);
    }
    group.items.push(item);
  }
  return groups;
}
